import * as React from 'react';

import classnames from 'classnames';

import {EmptyStateSmall} from '../empty-states/empty-state-small';
import {ErrorState} from '../error-state/error-state';
import {Illustration} from '../illustrations/illustration';
import {LoadingPage} from '../loading-page/loading-page';
import {useResizeObserver} from '../utils/hooks/use-resize-observer';

import {PaginatedTableControlBar} from './paginated-table-control-bar';
import {Table} from './table';
import {convertSortByToUrlSortParam} from './table-sort-helpers';
import type {Column} from './table-types';
import type {SortBy} from './types';

import styles from './paginated-table.module.css';

type Props<T> = {
    columns: Column<T>[];
    sortBy?: SortBy[];
    totalCount: number;
    fetchNextPage: () => Promise<void>;
    fetchPreviousPage: () => Promise<void>;
    updateUrlParamsOnSortChange?: (sortBy: string) => any;
    fetchFirstPageWithNewSort?: (sortBy: string) => Promise<void>;
    pageSize: number;
    buttons?: React.ReactNode;
    data: T[];
    itemSize?: number;
    emptyState?: React.ReactNode;
    fullWidth?: boolean;
    extendToFitWidth?: boolean;
    isLoading?: boolean;
    hasHeader?: boolean;
    renderRowActionMenu?: () => React.ReactElement;
    error?: string;
    // Disable virtualization for tables with variable height content.
    // When true, all rows render normally and scroll is handled by the container.
    disableVirtualization?: boolean;
};

export function PaginatedTable<T>(props: Props<T>) {
    const {
        fetchNextPage,
        fetchPreviousPage,
        updateUrlParamsOnSortChange,
        fetchFirstPageWithNewSort,
        error,
        isLoading,
        data,
        emptyState,
        itemSize = 40,
        disableVirtualization = false,
    } = props;

    const [currentPage, setCurrentPage] = React.useState(1);
    const [isFetchingPage, setIsFetchingPage] = React.useState(false);
    const [tableHeight, setTableHeight] = React.useState(0);
    const [theadRef, setTheadRef] = React.useState<HTMLElement | null>(null);

    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [, resizedContainerHeight] = useResizeObserver(containerRef.current);

    React.useEffect(() => {
        if (typeof resizedContainerHeight === 'number' && theadRef) {
            setTableHeight(resizedContainerHeight - theadRef.offsetHeight);
        }
    }, [resizedContainerHeight, theadRef]);

    const handleOnPageChange = React.useCallback(
        (requestedPage: number) => {
            setIsFetchingPage(true);
            if (currentPage < requestedPage) {
                fetchNextPage()
                    .then(() => {
                        setCurrentPage(requestedPage);
                    })
                    .finally(() => {
                        setIsFetchingPage(false);
                    });
            } else {
                fetchPreviousPage()
                    .then(() => {
                        setCurrentPage(requestedPage);
                    })
                    .finally(() => {
                        setIsFetchingPage(false);
                    });
            }
        },
        [currentPage, fetchNextPage, fetchPreviousPage]
    );

    const handleSortByChange = React.useCallback(
        (newSortBy: SortBy[]) => {
            const newSortString = convertSortByToUrlSortParam(newSortBy);
            if (!newSortString) {
                return;
            }

            if (updateUrlParamsOnSortChange) {
                updateUrlParamsOnSortChange(newSortString);
            }

            setIsFetchingPage(true);
            if (fetchFirstPageWithNewSort) {
                fetchFirstPageWithNewSort(newSortString)
                    .then(() => {
                        setCurrentPage(1);
                    })
                    .finally(() => {
                        setIsFetchingPage(false);
                    });
            }
        },
        [updateUrlParamsOnSortChange, fetchFirstPageWithNewSort]
    );

    const getTheadRef = React.useCallback((ref) => {
        setTheadRef(ref);
    }, []);

    const specialStates = React.useMemo(
        () => [
            {
                shouldRender: () => Boolean(error),
                component: <ErrorState errorMessage={error} />,
            },
            {
                shouldRender: () => Boolean(isLoading),
                component: <LoadingPage padding={4} />,
            },
            {
                shouldRender: () => !isLoading && data && data.length === 0,
                component: emptyState || (
                    <EmptyStateSmall
                        icon={
                            <div style={{width: 80}}>
                                <Illustration src='cactus-empty-state' width='100%' height='100%' />
                            </div>
                        }
                        title='Nothing was found'
                        subtitle='Try changing your filters or search query.'
                    />
                ),
            },
        ],
        [error, emptyState, isLoading, data]
    );

    const paginatedTableWrapperClassName = classnames(styles['paginated-table-wrapper'], {
        [styles['paginated-table-wrapper-with-header']]: props.hasHeader,
    });
    const virtualizeProps = React.useMemo(
        () =>
            disableVirtualization
                ? undefined
                : {
                      height: tableHeight,
                      itemSize: itemSize,
                      hasSingleBackgroundColor: true,
                      style: {
                          overflowX: 'hidden',
                          overflowY: 'auto',
                      },
                  },
        [tableHeight, itemSize, disableVirtualization]
    );

    const hasControlBar = props.totalCount > 0;

    return (
        // full-height is needed to allow for quick table height updates when user resizes the window
        <div className='overflow-y-hidden full-width full-height flex flex-dir-col'>
            {hasControlBar && (
                <PaginatedTableControlBar
                    currentPage={currentPage}
                    totalCount={props.totalCount}
                    onPageChange={handleOnPageChange}
                    disableButtons={isFetchingPage}
                    pageSize={props.pageSize}
                    buttons={props.buttons}
                />
            )}
            <div ref={containerRef} className={paginatedTableWrapperClassName}>
                {data && (
                    <Table
                        isResizable={true}
                        hasHeaderBorder={true}
                        hasHeaderCellBorders={true}
                        hasHoverState={false}
                        isCompact={true}
                        stickyHeaders={true}
                        hasBorders={true}
                        isCheckboxSelectable={false}
                        clickingSelectsRow={false}
                        disableFilters={true}
                        disableSorting={false}
                        columns={props.columns}
                        sortBy={props.sortBy}
                        disableSortRemove={true}
                        onChangeSortBy={
                            updateUrlParamsOnSortChange ? handleSortByChange : undefined
                        }
                        manualSortBy={true}
                        data={data || []}
                        extendToFitWidth={props.extendToFitWidth}
                        fullWidth={props.fullWidth}
                        specialStates={specialStates}
                        // This is the method we are using to get the header bar to actually stick in place
                        // Previously, and on other pages where we use this component, the table height does not respect the height of the
                        // parent component (in this case, the div above or the page container), it just is the height of all the rows.
                        // Because of this, the table does not handle its own scroll, rather the page it is on handles the scrolling. This means that
                        // the sticky header property is essentially ignored, and the header bar will scroll off the page.
                        // The way we handle this here is with the virtualizeProps, which sets the table height to a fixed size.
                        // This virtualizes the list using the FixedSizeList component (https://react-window.vercel.app/#/api/FixedSizeList) such
                        // that only the visible rows are rendered at a time. That way, the table itself handle the scroll, rather than the page, so it respects the sticky header.
                        // But in order to have it be a dynamic height, we have to use the useResizeObserver hook to set the height of the container
                        // and then pass that height to the virtualizeProps. It's a bit suboptimal, but it works and we are currently not sure of a better solution
                        // and possibly not worth the effort to find a better solution. Unless this becomes a problem, in which case we will have to do something different.
                        virtualizeProps={virtualizeProps}
                        getTheadRef={getTheadRef}
                        renderRowActionMenu={props.renderRowActionMenu}
                    />
                )}
            </div>
        </div>
    );
}
